import request from '@/utils/request'

/**
 * 用户登录
 * @param {Object} data 登录数据
 * @param {string} data.username 用户名
 * @param {string} data.password 密码
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     token: string,
 *     tokenType: string,
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     roles: Array<string>
 *   }
 * }>} 登录结果
 */
export function login(data) {
  return request({
    url: '/auth/login',
    method: 'post',
    data
  })
}

/**
 * 用户登出
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 登出结果
 */
export function logout() {
  return request({
    url: '/auth/logout',
    method: 'post'
  })
}

/**
 * 获取当前登录用户信息
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     userId: number,
 *     username: string,
 *     realName: string,
 *     roles: Array<string>,
 *     permissions: Array<string>
 *   }
 * }>} 用户信息
 */
export function getUserInfo() {
  return request({
    url: '/auth/info',
    method: 'get'
  })
}

/**
 * 用户注册
 * @param {Object} data 注册数据
 * @param {string} data.username 用户名，3-20个字符
 * @param {string} data.password 密码，至少6个字符
 * @param {string} data.realName 真实姓名
 * @param {string} [data.email] 邮箱
 * @param {string} [data.phone] 手机号，格式：1[3-9]开头的11位数字
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 注册结果
 */
export function register(data) {
  return request({
    url: '/auth/register',
    method: 'post',
    data
  })
}

/**
 * 修改密码
 * @param {Object} data 密码数据
 * @param {string} data.oldPassword 旧密码
 * @param {string} data.newPassword 新密码
 * @param {string} data.confirmPassword 确认密码
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 修改结果
 */
export function changePassword(data) {
  return request({
    url: '/auth/password',
    method: 'put',
    data
  })
}

/**
 * 获取用户列表
 * @param {Object} params 查询参数
 * @param {number} [params.pageNum=1] 页码
 * @param {number} [params.pageSize=10] 每页条数
 * @param {string} [params.username] 用户名
 * @param {string} [params.realName] 真实姓名
 * @param {number} [params.status] 状态：1-正常，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       username: string,
 *       realName: string,
 *       email: string,
 *       phone: string,
 *       status: number,
 *       roles: Array<string>,
 *       createTime: string,
 *       updateTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的用户列表
 */
export function getUserList(params) {
  return request({
    url: '/user/list',
    method: 'get',
    params
  })
}

/**
 * 获取用户详情
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 用户详情
 */
export function getUserById(id) {
  return request({
    url: `/user/${id}`,
    method: 'get'
  })
}

/**
 * 创建用户
 * @param {Object} data 用户数据
 * @param {string} data.username 用户名，4-50个字符
 * @param {string} [data.password] 密码，6-100个字符
 * @param {string} [data.realName] 真实姓名
 * @param {string} [data.email] 电子邮箱
 * @param {string} [data.phone] 手机号码，格式：1[3-9]开头的11位数字
 * @param {number} [data.status=1] 状态：1-正常，0-禁用
 * @param {Array<string>} [data.roles] 角色列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的用户信息
 */
export function createUser(data) {
  return request({
    url: '/user',
    method: 'post',
    data
  })
}

/**
 * 更新用户
 * @param {number} id 用户ID
 * @param {Object} data 用户数据
 * @param {string} data.username 用户名，4-50个字符
 * @param {string} [data.password] 密码，6-100个字符
 * @param {string} [data.realName] 真实姓名
 * @param {string} [data.email] 电子邮箱
 * @param {string} [data.phone] 手机号码，格式：1[3-9]开头的11位数字
 * @param {number} [data.status] 状态：1-正常，0-禁用
 * @param {Array<string>} [data.roles] 角色列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的用户信息
 */
export function updateUser(id, data) {
  return request({
    url: `/user/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除用户
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteUser(id) {
  return request({
    url: `/user/${id}`,
    method: 'delete'
  })
}

/**
 * 更新用户状态
 * @param {number} id 用户ID
 * @param {number} status 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 更新结果
 */
export function updateUserStatus(id, status) {
  return request({
    url: `/user/${id}/status`,
    method: 'put',
    params: { status }
  })
}

/**
 * 获取用户完整信息
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     permissions: Array<string>,
 *     bridges: Array<{
 *       id: number,
 *       bridgeId: number,
 *       bridgeName: string,
 *       bridgeCode: string
 *     }>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 用户完整信息
 */
export function getUserComplete(id) {
  return request({
    url: `/user/${id}/complete`,
    method: 'get'
  })
}

/**
 * 保存用户完整信息
 * @param {Object} data 用户完整信息
 * @param {number} data.id 用户ID
 * @param {string} data.username 用户名
 * @param {string} [data.password] 密码
 * @param {string} [data.realName] 真实姓名
 * @param {string} [data.email] 电子邮箱
 * @param {string} [data.phone] 手机号码
 * @param {number} [data.status] 状态：1-正常，0-禁用
 * @param {Array<string>} [data.roles] 角色列表
 * @param {Array<number>} [data.bridgeIds] 桥梁ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 保存结果
 */
export function saveUserComplete(data) {
  return request({
    url: '/user/complete',
    method: 'post',
    data
  })
}

/**
 * 获取所有角色
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     roleName: string,
 *     roleCode: string,
 *     description: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 所有角色列表
 */
export function getAllRoles() {
  return request({
    url: '/user/roles',
    method: 'get'
  })
}

/**
 * 获取指定用户角色
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<string>
 * }>} 用户角色列表
 */
export function getUserRoles(id) {
  return request({
    url: `/user/${id}/roles`,
    method: 'get'
  })
}

/**
 * 获取当前用户角色
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<string>
 * }>} 当前用户角色列表
 */
export function getCurrentUserRoles() {
  return request({
    url: `/user/current/roles`,
    method: 'get'
  })
}

/**
 * 分配用户角色
 * @param {number} id 用户ID
 * @param {Object} data 角色数据
 * @param {Array<string>} data.roleIds 角色ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function assignUserRoles(id, data) {
  return request({
    url: `/user/${id}/roles`,
    method: 'post',
    data
  })
}

/**
 * 获取角色列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     roleName: string,
 *     roleCode: string,
 *     description: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 角色列表
 */
export function getRoleList() {
  return request({
    url: '/roles',
    method: 'get'
  })
}

/**
 * 根据ID获取角色详情
 * @param {number} id 角色ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     roleName: string,
 *     roleCode: string,
 *     description: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 角色详情
 */
export function getRoleById(id) {
  return request({
    url: `/roles/${id}`,
    method: 'get'
  })
}

/**
 * 创建角色
 * @param {Object} data 角色数据
 * @param {string} data.roleName 角色名称
 * @param {string} data.roleCode 角色编码
 * @param {string} [data.description] 角色描述
 * @param {number} [data.status=1] 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     roleName: string,
 *     roleCode: string,
 *     description: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的角色信息
 */
export function createRole(data) {
  return request({
    url: '/roles',
    method: 'post',
    data
  })
}

/**
 * 更新角色
 * @param {number} id 角色ID
 * @param {Object} data 角色数据
 * @param {string} data.roleName 角色名称
 * @param {string} data.roleCode 角色编码
 * @param {string} [data.description] 角色描述
 * @param {number} [data.status] 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     roleName: string,
 *     roleCode: string,
 *     description: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的角色信息
 */
export function updateRole(id, data) {
  return request({
    url: `/roles/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除角色
 * @param {number} id 角色ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteRole(id) {
  return request({
    url: `/roles/${id}`,
    method: 'delete'
  })
}

/**
 * 获取角色权限
 * @param {number} id 角色ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<string>
 * }>} 角色权限列表
 */
export function getRolePermissions(id) {
  return request({
    url: `/roles/${id}/permissions`,
    method: 'get'
  })
}

/**
 * 分配角色权限
 * @param {number} id 角色ID
 * @param {Object} data 权限数据
 * @param {Array<string>} data.permissionIds 权限ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function assignRolePermissions(id, data) {
  return request({
    url: `/roles/${id}/permissions`,
    method: 'post',
    data
  })
}

/**
 * 获取用户权限
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<string>
 * }>} 用户权限列表
 */
export function getUserPermissions(id) {
  return request({
    url: `/user/${id}/permissions`,
    method: 'get'
  })
}

/**
 * 分配用户权限
 * @param {number} id 用户ID
 * @param {Object} data 权限数据
 * @param {Array<string>} data.permissionIds 权限ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function assignUserPermissions(id, data) {
  return request({
    url: `/user/${id}/permissions`,
    method: 'post',
    data
  })
}

/**
 * 获取用户关联的桥梁
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     userId: number,
 *     bridgeId: number,
 *     bridgeName: string,
 *     bridgeCode: string,
 *     latitude: number,
 *     longitude: number,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 用户关联的桥梁列表
 */
export function getUserBridges(id) {
  return request({
    url: `/user/${id}/bridges`,
    method: 'get'
  })
}

/**
 * 为用户分配桥梁
 * @param {number} id 用户ID
 * @param {Object} data 分配数据
 * @param {Array<number>} data.bridgeIds 桥梁ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function assignUserBridges(id, data) {
  return request({
    url: `/user/${id}/bridges`,
    method: 'post',
    data
  })
}

/**
 * 删除用户与桥梁的关联
 * @param {number} userId 用户ID
 * @param {number} bridgeId 桥梁ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function removeUserBridge(userId, bridgeId) {
  return request({
    url: `/user/${userId}/bridges/${bridgeId}`,
    method: 'delete'
  })
} 